import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { submitCSAT } from '@/api/csat'
import type { FC } from 'react'

interface CSATModalProps {
  isOpen: boolean
  sessionId: string
  onClose: () => void
}

const LABELS = ['Terrible', 'Bad', 'Okay', 'Good', 'Great']

export const CSATModal: FC<CSATModalProps> = ({ isOpen, sessionId, onClose }) => {
  const [rating, setRating] = useState(0)
  const [hovered, setHovered] = useState(0)
  const [comment, setComment] = useState('')
  const [phase, setPhase] = useState<'rate' | 'submitting' | 'done'>('rate')

  const handleSubmit = async () => {
    if (rating === 0) return
    setPhase('submitting')
    try {
      await submitCSAT(sessionId, rating, comment || undefined)
      setPhase('done')
    } catch {
      setPhase('rate')
    }
  }

  const handleClose = () => {
    setRating(0)
    setHovered(0)
    setComment('')
    setPhase('rate')
    onClose()
  }

  const active = hovered || rating

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="How was your experience?">
      <div className="cx-csat">
        {phase === 'done' ? (
          <div className="cx-csat-done">
            <span><Icon name="check" size={25} /></span>
            <p>Thanks for your feedback!</p>
            <small>It helps us improve CX Agent.</small>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <>
            <div className="cx-csat-stars" onMouseLeave={() => setHovered(0)}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={star <= active ? 'active' : ''}
                  style={{ color: star <= active ? '#f59e0b' : '#d1d5db' }}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHovered(star)}
                  aria-label={`Rate ${star} out of 5`}
                >
                  <Icon name="star" size={27} />
                </button>
              ))}
            </div>

            {active > 0 && (
              <p className="cx-csat-label">{LABELS[active - 1]}</p>
            )}

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="cx-csat-comment"
              placeholder="Tell us more (optional)…"
            />

            <div className="cx-csat-actions">
              <Button
                disabled={rating === 0 || phase === 'submitting'}
                onClick={handleSubmit}
              >
                {phase === 'submitting' ? 'Submitting…' : 'Submit feedback'}
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                Skip
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
